import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';

const MENU_MAX_HEIGHT = 440;
const SCROLL_FOOTER_HEIGHT = 30;

/**
 * Multi-select facet filter (e.g. model capability tags).
 *
 * @param {{ id: string, label: string, color?: string, count?: number }[]} facets
 * @param {Set<string>} selectedIds
 */
function CatalogFacetsDropdown({
  facets = [],
  selectedIds = new Set(),
  onToggle,
  onClearAll,
  defaultTriggerLabel = 'Capabilities',
  scrollForMoreLabel = 'Scroll for more',
  variant = 'default',
  style,
  menuScrollNativeId = 'catalog-facets-menu-scroll',
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const isChip = variant === 'toolbarChip';
  const isNeutral = variant === 'neutral';
  const scrollRef = useRef(null);
  const [showScrollFooter, setShowScrollFooter] = useState(false);

  const selectedCount = selectedIds?.size ?? 0;

  const triggerLabel = useMemo(() => {
    if (selectedCount === 0) return defaultTriggerLabel;
    if (selectedCount === 1) {
      const id = [...selectedIds][0];
      return facets.find((f) => f.id === id)?.label || defaultTriggerLabel;
    }
    return `${selectedCount} selected`;
  }, [selectedCount, selectedIds, facets, defaultTriggerLabel]);

  const activeTint = useMemo(() => {
    if (isNeutral || selectedCount !== 1) return colors.text_color_primary;
    const id = [...selectedIds][0];
    return facets.find((f) => f.id === id)?.color || colors.text_color_primary;
  }, [isNeutral, selectedCount, selectedIds, facets, colors.text_color_primary]);

  const updateScrollFooter = useCallback((offsetY, viewportH, contentH) => {
    if (!contentH || !viewportH) {
      setShowScrollFooter(false);
      return;
    }
    setShowScrollFooter(offsetY + viewportH < contentH - 8);
  }, []);

  const measureScrollFooter = useCallback((offsetY = 0) => {
    const node = scrollRef.current;
    if (!node) return;
    const viewportH = node.clientHeight ?? MENU_MAX_HEIGHT;
    const contentH = node.scrollHeight ?? 0;
    updateScrollFooter(offsetY, viewportH, contentH);
  }, [updateScrollFooter]);

  const handleScroll = useCallback((e) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    updateScrollFooter(contentOffset.y, layoutMeasurement.height, contentSize.height);
  }, [updateScrollFooter]);

  const handleContentSizeChange = useCallback(() => {
    requestAnimationFrame(() => measureScrollFooter(0));
  }, [measureScrollFooter]);

  const handleScrollLayout = useCallback(() => {
    requestAnimationFrame(() => measureScrollFooter(0));
  }, [measureScrollFooter]);

  const renderMenuItems = () => {
    const rows = facets.map((facet) => {
      const countSuffix = typeof facet.count === 'number' && facet.count > 0
        ? ` · ${facet.count}`
        : '';
      return (
        <DropdownMenuItem
          key={facet.id}
          icon="tag"
          title={`${facet.label}${countSuffix}`}
          isActive={selectedIds.has(facet.id)}
          onSelect={() => onToggle?.(facet.id)}
        />
      );
    });

    if (selectedCount > 0 && onClearAll) {
      rows.push(<DropdownMenu.Separator key="sep" className="DropdownMenuSeparator" />);
      rows.push(
        <DropdownMenuItem
          key="clear"
          icon="xmark"
          title="Clear filters"
          onSelect={onClearAll}
        />,
      );
    }

    return rows;
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Pressable
          style={({ hovered }) => [
            styles.trigger,
            isChip && styles.triggerChip,
            !isChip && isXS && styles.triggerCompact,
            isNeutral && { minHeight: isXS ? 42 : 32, paddingVertical: 0 },
            style,
            {
              backgroundColor: isNeutral
                ? (hovered ? colors.solid_color_tertiary : colors.solid_color_block)
                : isChip
                ? (selectedCount ? `${activeTint}12` : colors.solid_color_primary)
                : (hovered ? colors.solid_color_secondary_dark : colors.solid_color_secondary),
              borderColor: !isNeutral && selectedCount ? activeTint : colors.border_color_secondary,
            },
          ]}
          accessibilityRole="button"
          accessibilityLabel={triggerLabel}
        >
          <Icon
            name="layer-group"
            size={13}
            color={selectedCount ? activeTint : colors.text_color_secondary}
          />
          <Text
            size={isChip ? 'xs' : (isXS ? 'xs' : 's')}
            weight="medium"
            numberOfLines={1}
            style={[
              {
                color: selectedCount ? activeTint : colors.text_color_primary,
                maxWidth: isChip ? undefined : (isXS ? 96 : 140),
              },
              isChip && styles.triggerChipLabel,
            ]}
          >
            {triggerLabel}
          </Text>
          <Icon name="chevron-down" size={10} color={colors.text_color_tertiary} />
        </Pressable>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent CatalogCategoryDropdown' : undefined}
        align={isChip ? 'center' : 'end'}
        sideOffset={4}
        style={{ minWidth: 240, maxWidth: 300, padding: 4 }}
      >
        {Platform.OS === 'web' ? (
          <View style={styles.menuPanel}>
            <ScrollView
              ref={scrollRef}
              style={[
                styles.menuScroll,
                showScrollFooter && { maxHeight: MENU_MAX_HEIGHT - SCROLL_FOOTER_HEIGHT },
              ]}
              contentContainerStyle={styles.menuScrollContent}
              showsVerticalScrollIndicator
              nestedScrollEnabled
              scrollEventThrottle={16}
              onScroll={handleScroll}
              onContentSizeChange={handleContentSizeChange}
              onLayout={handleScrollLayout}
              nativeID={menuScrollNativeId}
            >
              {renderMenuItems()}
            </ScrollView>
            {showScrollFooter ? (
              <View
                style={[
                  styles.scrollFooter,
                  {
                    borderTopColor: colors.border_color_secondary,
                    backgroundColor: colors.solid_color_block,
                  },
                ]}
                pointerEvents="none"
              >
                <Icon name="chevron-down" size={9} color={colors.text_color_tertiary} />
                <Text size="xxs" weight="semibold" color="tertiary">
                  {scrollForMoreLabel}
                </Text>
              </View>
            ) : null}
          </View>
        ) : (
          facets.map((facet) => (
            <DropdownMenu.Item key={facet.id} onSelect={() => onToggle?.(facet.id)}>
              <DropdownMenu.ItemIcon ios={{ name: 'tag' }} />
              <DropdownMenu.ItemTitle>{facet.label}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          ))
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 1,
    maxWidth: 200,
    flexShrink: 1,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  triggerCompact: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    maxWidth: 132,
    gap: 5,
  },
  triggerChip: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    height: 36,
    justifyContent: 'center',
    paddingHorizontal: 10,
    borderRadius: 10,
    maxWidth: undefined,
  },
  triggerChipLabel: {
    flex: 1,
    minWidth: 0,
  },
  menuPanel: {
    maxHeight: MENU_MAX_HEIGHT,
    overflow: 'hidden',
  },
  menuScroll: {
    maxHeight: MENU_MAX_HEIGHT,
  },
  menuScrollContent: {
    flexGrow: 0,
    paddingVertical: 2,
  },
  scrollFooter: {
    height: SCROLL_FOOTER_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
});

export default React.memo(CatalogFacetsDropdown);
